import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';

// ── Nav links for the full variant ────────────────────────────────────────
const NAV_LINKS = [
  { label: 'Home',       to: '/'           },
  { label: 'Features',   to: '/features'   },
  { label: 'About',      to: '/about'      },
  { label: 'Blog',       to: '/blog'       },
  { label: 'ROMI Score', to: '/romi-score', badge: 'Free' },
];

/**
 * PublicNavbar — shared sticky navbar for all public/marketing pages.
 *
 * Props:
 *   activeRoute     string   — current route path (e.g. '/about') to highlight the active link
 *   user            object   — if provided, shows "Dashboard" instead of "Login"
 *   hasPilotAccess  boolean  — when false, hides the Login/Dashboard link (default: true)
 *   variant         'full' | 'minimal'
 *                            — 'full'    = logo + desktop links + mobile drawer
 *                            — 'minimal' = logo + a single right-side button
 *   backTo          string   — (minimal) destination for the right-side button
 *   backLabel       string   — (minimal) label for the right-side button
 */
const PublicNavbar = ({
  activeRoute     = '',
  user            = null,
  hasPilotAccess  = true,
  variant         = 'full',
  backTo          = '/',
  backLabel       = 'Back to Home',
}) => {
  const navigate  = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const drawerRef = useRef(null);

  // ── Body scroll lock ──────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ── Escape key close ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const goTo = (path) => { navigate(path); close(); };

  const loginDest  = user ? '/dashboard' : '/login';
  const loginLabel = user ? 'Dashboard'  : 'Login';

  return (
    <>
      {/* ── STICKY NAV BAR ─────────────────────────────────────────────── */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">

          {/* Logo — identical markup to all existing pages */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
            <span className="text-2xl font-bold text-cyan-600">
              NivasaPro.ai
            </span>
          </div>

          {variant === 'minimal' ? (
            /* Minimal right side — single back/action button */
            <Button
              variant="ghost"
              onClick={() => navigate(backTo)}
              className="text-gray-600 hover:text-cyan-600"
            >
              {backLabel}
            </Button>
          ) : (
            <>
              {/* ── DESKTOP NAV ────────────────────────────────────────── */}
              <div className="hidden md:flex items-center space-x-6">
                {NAV_LINKS.filter((l) => l.to !== '/').map(({ label, to, badge }) => (
                  <button
                    key={to}
                    onClick={() => navigate(to)}
                    className={`flex items-center gap-1.5 ${
                      activeRoute === to
                        ? 'text-cyan-600 font-medium'
                        : 'text-gray-600 hover:text-cyan-600 transition'
                    }`}
                  >
                    {label}
                    {badge && (
                      <span className="text-[10px] font-bold bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full leading-none">
                        {badge}
                      </span>
                    )}
                  </button>
                ))}
                <Button
                  variant="outline"
                  onClick={() => navigate(loginDest)}
                  className="border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-semibold px-5"
                >
                  {loginLabel}
                </Button>
              </div>

              {/* ── MOBILE HAMBURGER (hidden on md+) ─────────────────── */}
              <button
                className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-[5px] rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                onClick={() => setIsOpen((v) => !v)}
                aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isOpen}
                aria-controls="mobile-nav-drawer"
              >
                {/* Three lines → X animation */}
                <span
                  className="block h-0.5 w-5 bg-gray-700 rounded-full transition-all duration-300 origin-center"
                  style={{ transform: isOpen ? 'rotate(45deg) translateY(7px)' : 'none' }}
                />
                <span
                  className="block h-0.5 w-5 bg-gray-700 rounded-full transition-all duration-300"
                  style={{ opacity: isOpen ? 0 : 1 }}
                />
                <span
                  className="block h-0.5 w-5 bg-gray-700 rounded-full transition-all duration-300 origin-center"
                  style={{ transform: isOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }}
                />
              </button>
            </>
          )}
        </div>
      </nav>

      {/* ── DRAWER + OVERLAY (full variant only) ─────────────────────────── */}
      {variant === 'full' && (
        <>
          {/* Semi-transparent overlay — click closes drawer */}
          <div
            aria-hidden="true"
            onClick={close}
            className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-250 md:hidden"
            style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none' }}
          />

          {/* Slide-in drawer */}
          <div
            id="mobile-nav-drawer"
            ref={drawerRef}
            role="navigation"
            aria-label="Mobile navigation"
            aria-hidden={!isOpen}
            className="fixed top-0 right-0 z-50 h-full w-[75vw] max-w-[300px] flex flex-col md:hidden"
            style={{
              backgroundColor: '#0F4C5C',
              transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 250ms ease',
            }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
                <span className="text-base font-bold text-white tracking-tight">NivasaPro</span>
              </div>
              <button
                onClick={close}
                aria-label="Close navigation menu"
                className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>

            {/* Nav links — full width tap targets */}
            <div className="flex-1 flex flex-col overflow-y-auto">
              {NAV_LINKS.map(({ label, to, badge }, i) => (
                <button
                  key={to}
                  onClick={() => goTo(to)}
                  className={[
                    'w-full text-left px-6 py-4 text-sm font-medium transition-colors',
                    'hover:bg-white/10 focus:outline-none focus-visible:bg-white/10',
                    activeRoute === to ? 'text-white' : 'text-white/75 hover:text-white',
                    i < NAV_LINKS.length - 1 ? 'border-b border-white/10' : '',
                  ].join(' ')}
                >
                  <span className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {label}
                      {badge && (
                        <span className="text-[10px] font-bold bg-cyan-400/20 text-cyan-300 px-1.5 py-0.5 rounded-full leading-none">
                          {badge}
                        </span>
                      )}
                    </span>
                    {activeRoute === to && (
                      <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 flex-shrink-0" />
                    )}
                  </span>
                </button>
              ))}

              {/* Login / Dashboard CTA — always visible */}
              <div className="px-6 pt-5 pb-2">
                <button
                  onClick={() => goTo(loginDest)}
                  className="w-full py-3 rounded-xl bg-white text-sm font-semibold hover:bg-white/90 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                  style={{ color: '#0F4C5C' }}
                >
                  {loginLabel}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/10 flex-shrink-0">
              <p className="text-xs text-white/35">© 2025 NivasaPro</p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PublicNavbar;
